import { Inject, Injectable, Injector, Optional, Type } from "@angular/core";
import { Http } from "@angular/http";
import {
    IMessaging,
    IService,
    ITCIAppCustomizationProvider,
    ITCIApplicationCreateProvider,
    ITCIApplicationDeleteProvider,
    ITCIApplicationDeploymentProvider,
    ITCIApplicationEnvironmentProvider,
    ITCIApplicationMonitoringProvider,
    ITCIApplicationUpdateProvider,
    ITCIConnectionServiceProvider,
    ITCIEnvVars,
    ITCIExtensionProvider,
    ITCIMarketplaceServiceProvider,
    ITCIMenuInput,
    ITCITenantComponentOptions,
    ITCITenantServiceProvider,
    ITCIUserInfo,
    TCI_APP_MESSAGING,
    TCI_ENVIRONMENT_VAR_TYPE,
    TCI_TENANT_SERVICE_PROVIDER_TYPE,
    TCITenantAppType,
    TCITenantSelectorType,
    ITCITenantComponentInput,
    ITCISupplementServiceProvider, ITCIApplicationExecHistoryProvider
} from "@tci/common";
import { NgEnvVarsService } from "../ng-shared/ng-services/ng-env-vars.service";
import { TciTenantService } from "./tci-tenant.service";
import { Observable } from "rxjs";
import { CanLoadComponentFunction, InlineMenuItemHandlerFunction } from "./tci-tenant-component";

declare var Reflect;

const TCI_TENANT_SERVICES_SYMBOL = Symbol("@TCITenantServices");

/**
 * TCI Function Provider Map
 */
export interface ITCITenantServiceProviderMap {
    /**
     * Function Provider Type
     */
    serviceProviderType: TCI_TENANT_SERVICE_PROVIDER_TYPE;
    /**
     * Implementation Class
     */
    useClass: Type<ITCITenantServiceProvider>;
}

/**
 * TCI Component Loader Map
 */
export interface ITCITenantComponentPreLoadMap {
    selector: TCITenantSelectorType;

    /**
     * Can Load Component function
     * Check before loading the component
     * Default is always loadable and returns true
     * @param input
     * @example
     * ```
     *  canLoadComponent: <A extends TCI_MENU_TYPE, B extends ITCITenantComponentOptions<A>, C extends ITCIAppDetailMenuInput>
     * (options: B, menuInput: C): boolean => {
     *   return true;
     * }
     * ```
     */
    canLoadComponentFn?: CanLoadComponentFunction<any, any, any>;

    /**
     * For inline menu item component with a
     * On click of the inline menu item, this
     * handler function will be called with the same
     * input that is also given to canLoadComponentFunction
     * to allow the tenant to do its work
     */
    inlineMenuHandlerFn?: InlineMenuItemHandlerFunction<any, any, any>;
    /**
     * URL suffix for externally loaded iframe UI
     * The item will carry the main url and the tabs will carry the suffix
     * e.g. App id `x` points to `https://externaldomain/.../<appid>/<externalUrlSuffix>
     */
    externUrlSuffix?: string;
}

/**
 * Preload ComponentMetaData
 */
export interface ITCITenantComponentPreloadMetadata {
    /**
     * tenant preload component access check function
     */
    canLoadComponentFn?: CanLoadComponentFunction<any, any, any>;

    /**
     * For inline menu item component with a selector
     */
    inlineMenuHandlerFn?: InlineMenuItemHandlerFunction<any, any, any>;

    /**
     * For LiveApps iFrame
     */
    externUrlSuffix?: string;
}

/**
 * Map of tenant selectors and ComponentMetaData
 */
export interface ITCITenantComponentsPreloadMap {
    [key: string]: ITCITenantComponentPreloadMetadata;
}

/**
 * TCI Tenant Annotation metadata
 */
export interface ITCITenantServicesMetaData {
    /**
     * Type of tenant
     */
    tenant_type: TCITenantAppType;
    /**
     * Array of function Providers from a tenant
     * A tenant may  implement one or more function providers or none
     */
    serviceProviders?: ITCITenantServiceProviderMap[];
    /**
     * component pre-load map
     */
    componentPreLoadMap?: ITCITenantComponentPreLoadMap[];
}


/**
 * interface ITCITenantServices
 */
export interface ITCITenantServices {
    /**
     * initialize
     */
    init(info: ITCIUserInfo): Observable<boolean>;

    /**
     * Type of tenant
     */
    getTenantType(): TCITenantAppType;

    /**
     * Get the application environment provider
     */
    getAppEnvironmentProvider(): ITCIApplicationEnvironmentProvider;

    /**
     * Get the application create provider
     */
    getAppCreateProvider(): ITCIApplicationCreateProvider;

    /**
     * Get the application update provider
     */
    getAppUpdateProvider(): ITCIApplicationUpdateProvider;

    /**
     * Get the Application Deployment Provider
     */
    getAppDeploymentProvider(): ITCIApplicationDeploymentProvider;

    /**
     * Get the Application Delete Provider
     */
    getAppDeleteProvider(): ITCIApplicationDeleteProvider;

    /**
     * Get the Application MarketPlace Provider
     */
    getAppExtensionProvider(): ITCIExtensionProvider;

    /**
     * Get the app customization provider
     * only one tenant can customize e.g. FE
     */
    getAppCustomizationProvider(): ITCIAppCustomizationProvider;

    /**
     * Get the connection service provider responsible for connection life cycle
     */
    getConnectionServiceProvider(): ITCIConnectionServiceProvider;

    /**
     * Get the app monitoring service provider
     */
    getMonitoringServiceProvider(): ITCIApplicationMonitoringProvider;

    /**
     * Get the marketplace service provider
     */
    getMarketplaceServiceProvider(): ITCIMarketplaceServiceProvider;

    /**
     * Get the tenant Supplement service provider
     */
    getSupplementServiceProvider(): ITCISupplementServiceProvider;

    /**
     * Get the exec history service provider
     */
    getExecHistoryServiceProvider(): ITCIApplicationExecHistoryProvider;

    /**
     * Get CanLoadComponentFunction for a given selector
     * @param selector
     */
    getCanLoadComponentFn<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput>
    (selector: TCITenantSelectorType): CanLoadComponentFunction<M, O, T>;


    /**
     * Get External URL suffix for a given selector
     * URL suffix for externally loaded iframe UI
     * The item will carry the main url and the tabs will carry the suffix
     * e.g. App id `x` points to `https://externaldomain/.../<appid>/<externalUrlSuffix>
     * @param selector
     */
    getExternalUrlSuffix<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput>
        (selector: TCITenantSelectorType): string;

    /**
     * Get InlineMenuItemHandlerFunction for a given selector
     * @param selector
     */
    getInlineMenuHandlerFn<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput>
        (selector: TCITenantSelectorType): InlineMenuItemHandlerFunction<M, O, T>;

    /**
     * Get a map of tenant component selectors and can load component function
     * This map will tell TCI which components are implemented per tenant to
     * optimize component cache loading by tenant. Component Cache loading will
     * be on demand lazy loading per tenant. i.e. when opening app detail for flogo
     * there is no need to load UI components for another tenant.
     */
    getTenantComponentsPreloadMap(): ITCITenantComponentsPreloadMap;
}

/**
 * interface ITCITenantModelService
 */
export interface ITCITenantModelService {
    /**
     * Bind the tenant service to the model service
     * @param service
     */
    bindTenantService(service: TciTenantService);

    /**
     * Notifies the subscriber when the tenant modules are initialized
     */
    onInit(): Observable<boolean>;

    /**
     * Returns the tenant mapping
     */
    getTenants(): ITenantMap;

    /**
     * Returns a Tenant for the given id
     * @param id
     */
    getTenant(id: TCITenantAppType): ITCITenantServices;

    /**
     * Add a tenant to the model service
     */
    addTenant(id: TCITenantAppType, tenant: ITCITenantServices);

    /**
     * Returns a ModelEnvironment Value
     * @param envName
     * @return Observable<T>
     */
    getModelEnvironment<T>(envName: TCI_ENVIRONMENT_VAR_TYPE): Observable<T>;

}

/**
 * interface ITenantMap
 * [ id: @string] : @ITCITenant
 */
export interface ITenantMap {
    [id: string]: ITCITenantServices;
}

@Injectable()
export class TciTenantModelService implements ITCITenantModelService {
    private static instance: TciTenantModelService = null;
    protected tenantService: TciTenantService;
    protected tenantMap: ITenantMap = {};
    private isInit = false;

    public static getInstance(messagingService: IMessaging, envVars: NgEnvVarsService): TciTenantModelService {
        if (!TciTenantModelService.instance) {
            TciTenantModelService.instance = new TciTenantModelService(messagingService, envVars);
        }
        return TciTenantModelService.instance;
    }

    constructor(
        @Inject("ngAppMessaging") private messagingService: IMessaging,
        private envVars: NgEnvVarsService
    ) {
    }

    bindTenantService(service: TciTenantService) {
        if (!this.tenantService) {
            this.tenantService = service;
        }
        this.isInit = true; // Set the flag here to resolve those "onInit" subscriptions not yet made
        this.messagingService.emit(TCI_APP_MESSAGING.TCI_TENANT_SVC_INIT_COMPLETE, true);
    }

    public onInit(): Observable<boolean> {
        if (!this.isInit) {
            return Observable.create(obs => {
                this.messagingService.on(TCI_APP_MESSAGING.TCI_TENANT_SVC_INIT_COMPLETE, (res: boolean) => {
                    if (res) {
                        obs.next(res);
                        obs.complete();
                    }
                });
            });
        } else {
            return Observable.of(this.isInit);
        }
    }

    public addTenant(id: TCITenantAppType, tenant: ITCITenantServices) {
        this.tenantMap[id.toString()] = tenant;
    }

    public getTenant(id: TCITenantAppType): ITCITenantServices {
        return this.tenantMap[id.toString()];
    }

    public getTenants(): ITenantMap {
        return this.tenantMap;
    }

    /* public isTenantAvailable(id: TCITenantAppType): Observable<boolean> {
        const check = () => typeof this.tenantMap[id.toString()] !== "undefined";
        if (this.tenantService) {
            return Observable.of(check());
        } else {
            return Observable.create(obs => {
                this.messagingService.on(TCI_APP_MESSAGING.TCI_TENANT_SVC_INIT_COMPLETE, () => {
                    obs.next(check());
                    obs.complete();
                    this.messagingService.off(TCI_APP_MESSAGING.TCI_TENANT_SVC_INIT_COMPLETE);
                });

            });
        }
    } */

    /**
     * Returns a ModelEnvironment Value
     * @param envName
     * @return Observable<T>
     */
    getModelEnvironment<T>(envName: TCI_ENVIRONMENT_VAR_TYPE): Observable<T> {
        return this
            .envVars
            .getEnvVar(envName)
            .map((envVars: ITCIEnvVars<T>) => {
                return envVars[envName];
            });
    }
}

/**
 * The [[TciTenant]] being the abstract base class is extended  by a tenant provider
 * The Tenant provider provides @TciTenant({}) annotation based on the @ITCITenantMetaData.
 * The annotation describes the tenant type and an array of Function providers
 * Each function provider has its own interface for input and output
 *  ### Example [[TciTenant]]  Typescript file
 *  ```
 *  import {Injectable, Injector} from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import {TciTenant} from "@tci/core";
 *  import {Http} from "@angular/http";
 *
 *  @TciTenant({
 *      tenantType: TCI_TENANT_APP_TYPE.flogo,
 *      functionProviders:[
 *          {
 *              functionProviderType: TCI_TENANT_SERVICE_PROVIDER_TYPE.IMPORT,
 *              useClass: WiStudioApplicationImport
 *          },
 *          {
 *              functionProviderType: TCI_TENANT_SERVICE_PROVIDER_TYPE.DELETE,
 *              useClass: WiStudioApplicationDelete
 *          }
 *      ]
 *
 *  })
 *  @Injectable()
 *  export class WiStudioTenant extends TciTenant {
 *      constructor(@Inject(Injector) private _injector?: Injector,
                @Inject(Http) private _http?: Http,
                @Inject(TciTenantModelService) private _modelService?: ITCITenantModelService) {
 *       super(injector, http, _modelService);
 *   }
 *
 *  ```
 *  This tenant needs to be exposed from the `${tenant-name}-svc` entry point module as
 *  @NgModule({
 *   imports: [
 *       HttpModule
 *   ],
 *   providers: [
 *       {
 *           provide: TciTenant,
 *           useClass: WiStudioTenant
 *       }
 *   ]
 * })
 * export default class TenantChildAppModule {
 * }
 */
export abstract class AbstractTciTenant implements ITCITenantServices {
    /**
     * constructor
     * @param _injector
     * @param _http
     * @param _modelService
     */
    constructor(
        @Inject(Injector) private _injector?: Injector,
        @Inject(Http) private _http?: Http,
        @Inject(TciTenantModelService) private _modelService?: ITCITenantModelService
    ) {
    }

    /**
     * initialize for tenants to override
     */
    init(info: ITCIUserInfo): Observable<boolean> {
        return Observable.of(true);
    }

    /**
     * get annotation from metadata
     * @protected
     */
    protected getAnnotations(): ITCITenantServicesMetaData {
        const metadata = <ITCITenantServicesMetaData>Reflect.getMetadata(
            "custom:annotation",
            this.constructor,
            TCI_TENANT_SERVICES_SYMBOL
        );
        return metadata;
    }

    /**
     * Returns the injector
     * @protected
     * @return {Injector}
     */
    public getInjector(): Injector {
        return this._injector;
    }

    /**
     * Returns the [[Http]] object
     * @protected
     * @return {Http}
     */
    protected getHttp(): Http {
        return this._http;
    }

    /**
     * Returns the [[TCI]] object
     * @return {ITCITenantModelService}
     */
    protected getModelService(): ITCITenantModelService {
        return this._modelService;
    }

    getTenantType(): TCITenantAppType {
        const metadata = this.getAnnotations();
        if (metadata.tenant_type) {
            return metadata.tenant_type;
        }
        return null;
    }

    private getServiceProviderByType<T>(providerType: TCI_TENANT_SERVICE_PROVIDER_TYPE): T {
        const metadata = this.getAnnotations();
        if (metadata.serviceProviders && metadata.serviceProviders.length > 0) {
            for (let i = 0; i < metadata.serviceProviders.length; i++) {
                if (metadata.serviceProviders[i].serviceProviderType === providerType) {
                    return this.getInjector()
                        .get(metadata.serviceProviders[i].useClass);
                }
            }
        }
        return null;
    }

    /**
     * Get the application create provider
     */
    getAppEnvironmentProvider(): ITCIApplicationEnvironmentProvider {
        return this.getServiceProviderByType<ITCIApplicationEnvironmentProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_ENV);
    }

    /**
     * Get the application create provider
     */
    getAppCreateProvider(): ITCIApplicationCreateProvider {
        return this.getServiceProviderByType<ITCIApplicationCreateProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_CREATE);
    }

    /**
     * Get the application create provider
     */
    getAppUpdateProvider(): ITCIApplicationUpdateProvider {
        return this.getServiceProviderByType<ITCIApplicationUpdateProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_UPDATE);
    }

    /**
     * Get the application deployment provider
     */
    getAppDeploymentProvider(): ITCIApplicationDeploymentProvider {
        return this.getServiceProviderByType<ITCIApplicationDeploymentProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DEPLOY);
    }

    /**
     * Get the Application Delete Provider
     */
    getAppDeleteProvider(): ITCIApplicationDeleteProvider {
        return this.getServiceProviderByType<ITCIApplicationDeleteProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DELETE);
    }

    /**
     * Get the Application MarketPlace Provider
     */
    getAppExtensionProvider(): ITCIExtensionProvider {
        return this.getServiceProviderByType<ITCIExtensionProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_EXTENSION);
    }

    /**
     * Get the Application MarketPlace Provider
     */
    getAppCustomizationProvider(): ITCIAppCustomizationProvider {
        return this.getServiceProviderByType<ITCIAppCustomizationProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_CUSTOMIZE);
    }

    /**
     * Get the connection service provider
     */
    getConnectionServiceProvider(): ITCIConnectionServiceProvider {
        return this.getServiceProviderByType<ITCIConnectionServiceProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.CONNECTION_SERVICE);
    }

    /**
     * Get the app monitoring service provider
     */
    getMonitoringServiceProvider(): ITCIApplicationMonitoringProvider {
        return this.getServiceProviderByType<ITCIApplicationMonitoringProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_MONITORING);
    }

    /**
     * Get the connection service provider
     */
    getMarketplaceServiceProvider(): ITCIMarketplaceServiceProvider {
        return this.getServiceProviderByType<ITCIMarketplaceServiceProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.MARKETPLACE);
    }

    /**
     * Get the tenant Supplement service provider
     */
    getSupplementServiceProvider(): ITCISupplementServiceProvider {
        return this.getServiceProviderByType<ITCISupplementServiceProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.SUPPLEMENTS);
    }

    /**
     * Get the exec history service provider
     */
    getExecHistoryServiceProvider(): ITCIApplicationExecHistoryProvider {
        return this.getServiceProviderByType<ITCIApplicationExecHistoryProvider>(TCI_TENANT_SERVICE_PROVIDER_TYPE.EXEC_HISTORY);
    }

    /**
     * Get CanLoadComponentFunction for a given selector
     * @param selector
     */
    getCanLoadComponentFn<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput>
    (selector: TCITenantSelectorType): CanLoadComponentFunction<M, O, T> {
        const defaultCanLoadComponentFn = (options: O, input: T, injector: Injector) => {
            return Observable.of(true);
        };
        const metadata = this.getAnnotations();
        if (metadata.componentPreLoadMap && metadata.componentPreLoadMap.length > 0) {
            for (let i = 0; i < metadata.componentPreLoadMap.length; i++) {
                if (metadata.componentPreLoadMap[i].selector.isEqualTo(selector.toString())) {
                    return metadata.componentPreLoadMap[i].canLoadComponentFn ?
                        metadata.componentPreLoadMap[i].canLoadComponentFn : defaultCanLoadComponentFn;
                }
            }
        }
        return defaultCanLoadComponentFn;
    }

    /**
     * Get External URL suffix for a given selector
     * URL suffix for externally loaded iframe UI
     * The item will carry the main url and the tabs will carry the suffix
     * e.g. App id `x` points to `https://externaldomain/.../<appid>/<externalUrlSuffix>
     * @param selector
     */
    getExternalUrlSuffix<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput>
        (selector: TCITenantSelectorType): string {
        const defaultSuffix = "";
        const metadata = this.getAnnotations();
        if (metadata.componentPreLoadMap && metadata.componentPreLoadMap.length > 0) {
            for (let i = 0; i < metadata.componentPreLoadMap.length; i++) {
                if (metadata.componentPreLoadMap[i].selector.isEqualTo(selector.toString())) {
                    return metadata.componentPreLoadMap[i].externUrlSuffix ?
                        metadata.componentPreLoadMap[i].externUrlSuffix : defaultSuffix;
                }
            }
        }
        return defaultSuffix;
    }

    /**
     * Get InlineMenuItemHandlerFunction for a given selector
     * @param selector
     */
    getInlineMenuHandlerFn<M, O extends ITCITenantComponentOptions<M>, T extends ITCIMenuInput>
        (selector: TCITenantSelectorType): InlineMenuItemHandlerFunction<M, O, T> {
        const metadata = this.getAnnotations();
        if (metadata.componentPreLoadMap && metadata.componentPreLoadMap.length > 0) {
            for (let i = 0; i < metadata.componentPreLoadMap.length; i++) {
                if (metadata.componentPreLoadMap[i].selector.isEqualTo(selector.toString())) {
                    return metadata.componentPreLoadMap[i].inlineMenuHandlerFn ?
                        metadata.componentPreLoadMap[i].inlineMenuHandlerFn : null;
                }
            }
        }
        return null;
    }

    /**
     * Get a map of tenant component selectors and can load component function
     */
    getTenantComponentsPreloadMap(): ITCITenantComponentsPreloadMap {
        const result = {};
        const defaultCanLoadComponentFn = (options: any, input: any, injector: Injector) => {
            return Observable.of(true);
        };
        const metadata = this.getAnnotations();
        if (metadata.componentPreLoadMap && metadata.componentPreLoadMap.length > 0) {
            for (let i = 0; i < metadata.componentPreLoadMap.length; i++) {
                const preloadmetadata = {} as ITCITenantComponentPreloadMetadata;
                if (metadata.componentPreLoadMap[i].selector) {
                    const key = metadata.componentPreLoadMap[i].selector.toString();
                    preloadmetadata.canLoadComponentFn = metadata.componentPreLoadMap[i].canLoadComponentFn ?
                        metadata.componentPreLoadMap[i].canLoadComponentFn : defaultCanLoadComponentFn;
                    preloadmetadata.inlineMenuHandlerFn = metadata.componentPreLoadMap[i].inlineMenuHandlerFn ?
                        metadata.componentPreLoadMap[i].inlineMenuHandlerFn : null;
                    preloadmetadata.externUrlSuffix = metadata.componentPreLoadMap[i].externUrlSuffix ?
                        metadata.componentPreLoadMap[i].externUrlSuffix : null;
                    result[key] = preloadmetadata;
                } else {
                    console.error("Selector is not present", i);
                }
            }
        }
        return result;
    }
}

/**
 * @description This decorator and annotation indicates the typescript class is a Web Integrator contribution
 * ## Example
 * ```
 *
 *   @WiContrib()
 *   class RestService {}
 * ```
 *
 *
 * @param {WiContribMetaData} metadata
 * @return {(target: Function) => void}
 * @constructor
 */
export function TCITenantServices(metadata: ITCITenantServicesMetaData): (target: Function) => void {
    return (target: Function): any => {
        // console.log("WiContrib(" + metadata + ") called on: ", target);
        Reflect.defineMetadata("custom:annotation", metadata, target, TCI_TENANT_SERVICES_SYMBOL);
        return target;
    };
}

export const LOCAL_TENANT_SVCS: IService[] = [];

